import { transformCart } from "../../cart/helpers/cartApiHelpers/transformCart";
import type { RawOrderResponse } from "../../../../types/api/wcStoreApi/wcStoreApiOrderEndpointsTypes";
import type { TransformedOrder } from "../types/orderApiTypes";

interface LanguageInfo {
  currentLanguage: string;
  shouldTranslate: boolean;
}

/**
 * Transforms the raw Order API response.
 * Uses the transformCart logic as a base and enriches it with order-specific metadata.
 */
export const transformOrderResponse = (
  response: RawOrderResponse,
  languageInfo: LanguageInfo
): TransformedOrder => {
  // Use the cart transformer to handle totals, items, addresses, and the delivery_estimation extension.
  // We cast to any because the Order and Cart raw structures are compatible for transformation purposes.
  const transformedCartData = transformCart(response as any, languageInfo);

  // Patch item IDs specifically for orders (resolving Variation vs Product ID)
  const patchedItems = transformedCartData.items.map((item, index) => {
    const rawItem = response.items[index];
    const idsExtension = rawItem.extensions?.item_ids;

    if (idsExtension) {
      const realId =
        idsExtension.variation_id > 0
          ? idsExtension.variation_id
          : idsExtension.product_id;

      return {
        ...item,
        id: realId,
        parentId: idsExtension.parent_id,
      };
    }
    return item;
  });

  const additionalInfo = response.extensions?.additional_order_info;
  const agreementsSnapshot = response.extensions?.legal_agreements_snapshot;

  let formattedDate = "";
  if (additionalInfo?.date_created) {
    try {
      formattedDate = new Date(additionalInfo.date_created).toLocaleDateString(
        languageInfo.currentLanguage.replace("_", "-"),
        {
          year: "numeric",
          month: "long",
          day: "numeric",
        }
      );
    } catch (e) {
      formattedDate = additionalInfo.date_created.split("T")[0];
    }
  }

  return {
    ...transformedCartData,
    items: patchedItems,
    id: response.id,
    status: response.status,
    orderNumber: additionalInfo?.order_number || String(response.id),
    dateCreated: formattedDate,
    customerNote: additionalInfo?.customer_note || "",
    paymentMethodInfo: {
      methodId: additionalInfo?.payment_method_info?.method_id || "",
      title: additionalInfo?.payment_method_info?.title || "",
      instructions: additionalInfo?.payment_method_info?.instructions || "",
      accountDetails:
        additionalInfo?.payment_method_info?.account_details || [],
    },
    legalAgreementsSnapshot: agreementsSnapshot
      ? {
          preliminaryHtml: agreementsSnapshot.preliminary_html,
          salesHtml: agreementsSnapshot.sales_html,
        }
      : undefined,
  };
};
